import { call, isFn } from 'rise-ui/utils/function.js';
import { isSet, isUnset } from 'rise-ui/utils/nullish.js';
import { isEmberDataModelArray } from 'rise-ui/utils/promise.js';

/**
 * Create and fill an array
 * @param {number} size
 * @param {function(number):*|*} valueOrCallback Value to fill or callback to map each value (default: fill with `undefined`)
 * @returns {*[]}
 */
export function createArray(size, valueOrCallback = undefined) {
  if (typeof valueOrCallback !== 'function') {
    const v = valueOrCallback;
    valueOrCallback = () => v;
  }
  const array = [];
  for (let i = 0; i < size; ++i) {
    array[i] = valueOrCallback(i);
  }
  return array;
}

/**
 * Whether given value as an array
 * @param {[]|ArrayProxy|*} value Array or value to wrap in array
 * @returns {boolean}
 */
export function isArray(value) {
  return value && (Array.isArray(value) || isFn(value.toArray));
}

function nonArrayToArray(value) {
  return [].concat(value);
}

/**
 * Returns given value as an array
 * @param {[]|ArrayProxy|Set|*} value Array or value to wrap in array
 * @param {boolean} [copy] Whether to return a copy if it is an actual array
 * @param {*|function} [defaultValue] Default value to return if value is of
 * non-array type (default: wrap value in array. Note: use a function to return
 * `null` or `undefined`, or the default implementation will be used instead)
 * @returns {[]}
 */
export function toArray(value, copy = false, defaultValue = null) {
  defaultValue ??= nonArrayToArray;
  return isUnset(value) ? []
    : (Array.isArray(value)
      ? !copy && value
      : isEmberDataModelArray(value) ? value.content
      : isFn(value.toArray) ? (() => {
          // debugger; // used to debug which objects are activating deprecation warning
          return value.toArray();
        })()
      : typeof value !== 'string' && (
        typeof value[Symbol.iterator] === 'function'
        || Object.prototype.hasOwnProperty.call(value, 'length')
      ) && Array.from(value)
    ) || call(defaultValue, defaultValue, value);
}

/**
 * Concatenates given values or arrays into an array
 * @param {Array.<[]|ArrayProxy|*>} values Arrays or values to concatenate
 * @returns {[]}
 */
export function concat(...values) {
  return [].concat(...values.map(v => toArray(v)));
}

/**
 * @typedef {{unique:[],missing:[]}} ArrayDiffResult
 */
/**
 * Compares given values against each other
 * @param {Array.<[]|ArrayProxy|*>} values Arrays or values to compare
 * @returns {ArrayDiffResult[]} Array of differences parallel to arguments
 */
export function diff(...values) {
  const valuesToIndexes = new Map();
  const results = values.map((value, index) => {
    value = toArray(value);
    value.forEach(v => {
      const indexes = valuesToIndexes.get(v) || new Set();
      indexes.add(index);
      valuesToIndexes.set(v, indexes);
    });
    return { values: value, unique: [], missing: [] };
  });
  valuesToIndexes.forEach((indexes, v) => {
    if (indexes.size === 1) {
      results[Array.from(indexes)[0]].unique.push(v);
    }
    values.forEach((val, i) => {
      if (!indexes.has(i)) {
        results[i].missing.push(v);
      }
    });
  });
  return results;
}

export const extentOne = (value, previous) => {
  let [ min, max ] = previous || [ null, null ];
  return [ minOne(value, min), maxOne(value, max) ];
};

export const sumOne = (value, previous) => {
  return +(previous || 0) + +(value || 0);
};

export const minOne = (value, min) => {
  return isSet(min) ? isUnset(value) || min < value ? min : value : value;
};

export const maxOne = (value, max) => {
  return isSet(max) ? isUnset(value) || max > value ? max : value : value;
};
